/**
 * SEO metadata helpers.
 *
 * Competition copy is authored for humans in the backoffice, so `description` arrives as
 * free-form HTML that may be a bare fragment ("Includes:") or several paragraphs. Search
 * engines want one sentence inside a narrow length band and rewrite anything outside it,
 * which is why the normalisation lives here instead of being inlined per page.
 */

export const DESC_MIN = 110
export const DESC_MAX = 158
export const TITLE_MAX = 60

/** Openers that carry no meaning once the rest of the copy is cut away. */
const LEADING_NOISE = /^(includes?|including|contains?|features?|what'?s included|the following|for \d+ (?:people|persons?)|you (?:will |'ll )?(?:get|receive))\b\s*:?\s*/i

const stripHtml = (s: string) => s.replace(/<[^>]*>/g, ' ')
const decodeBasicEntities = (s: string) =>
  s.replace(/&nbsp;/gi, ' ').replace(/&amp;/gi, '&').replace(/&quot;/gi, '"').replace(/&#39;|&apos;/gi, "'").replace(/&lt;/gi, '<').replace(/&gt;/gi, '>')
const collapse = (s: string) => s.replace(/\s+/g, ' ').trim()

/** Cut at the last word boundary that fits so a snippet never ends mid-word. */
export function truncateAtWord(text: string, max: number): string {
  const clean = collapse(text)
  if (clean.length <= max) return clean
  const cut = clean.slice(0, max - 1)
  const lastSpace = cut.lastIndexOf(' ')
  // Only honour the boundary if it isn't so early that we'd throw most of the text away.
  const kept = lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut
  return kept.replace(/[\s,;:.\-–—]+$/, '') + '…'
}

/** Titles are hard-capped: Google truncates past ~60 chars and rewrites the rest. */
export function truncateTitle(title: string, max: number = TITLE_MAX): string {
  return truncateAtWord(title, max)
}

/** Normalise backoffice copy into a single plain-text sentence-ish line. */
export function normaliseCopy(raw?: string | null): string {
  if (!raw) return ''
  const flat = collapse(decodeBasicEntities(stripHtml(raw)))
  // Take the first paragraph only — later ones are terms, delivery notes, etc.
  let out = collapse(flat.split(/(?:\.\s|•|\|)/)[0] || flat)
  // Strip repeatedly: "Includes the following for two people:" sheds two prefixes, not one.
  let prev = ''
  while (out && out !== prev) {
    prev = out
    out = collapse(out.replace(LEADING_NOISE, ''))
  }
  return out.replace(/[:;,\-–—]+$/, '').trim()
}

/** "0.4900" -> "0.49", "1.00" -> "1", "2.5000" -> "2.50"; non-numeric is left untouched. */
function formatTicketPrice(raw?: string): string {
  if (!raw) return ''
  const n = Number(raw)
  if (!Number.isFinite(n)) return raw
  // A price shown to a human needs both pence digits or none at all — "£2.5" reads as a typo.
  return Number.isInteger(n) ? String(n) : n.toFixed(2)
}

export type CompetitionMetaInput = {
  prizeName?: string
  ticketPrice?: string
  currencySymbol?: string
  closesOn?: string
  rawDescription?: string | null
  brandName: string
  /** Site-wide description, used when there is nothing competition-specific to say. */
  fallback: string
}

/**
 * Build a competition meta description.
 *
 * Prefers the editor's own copy when it is substantial enough to stand alone, otherwise
 * composes a template from the prize, ticket price and closing date. Either way the result
 * lands inside [DESC_MIN, DESC_MAX] where possible, so Google shows it verbatim.
 */
export function buildCompetitionDescription(input: CompetitionMetaInput): string {
  const { prizeName, ticketPrice, currencySymbol = '£', closesOn, rawDescription, brandName, fallback } = input

  const editorial = normaliseCopy(rawDescription)
  if (editorial.length >= DESC_MIN) return truncateAtWord(editorial, DESC_MAX)

  if (!prizeName) return truncateAtWord(fallback, DESC_MAX)

  // Most prize names are bare ("Xbox Series X") but some are authored as a full line that
  // already opens with the verb ("Win £50,000 OR £1,000 A Week"), which produced "Win Win …".
  const name = collapse(prizeName)
  const parts = [/^win\b/i.test(name) ? name : `Win ${name}`]
  // Price arrives from the API as a padded decimal ("0.4900"); show it the way the Product
  // JSON-LD on the same page already does, so the snippet reads "£0.49" not "£0.4900".
  const price = formatTicketPrice(ticketPrice)
  if (price) parts.push(`from ${currencySymbol}${price} a ticket`)
  let line = parts.join(' ') + '.'
  if (closesOn) line += ` Entries close ${closesOn}.`

  // Pad with the brand's positioning so short prize names still reach a usable length.
  // Several lengths, longest first — a verbose prize name leaves little room, a bare
  // "Xbox Series X" leaves plenty, and both should still land above DESC_MIN.
  const tails = [
    ` Low-odds car, cash and tech competitions across the UK and Ireland at ${brandName}.`,
    ` Low-odds competitions across the UK and Ireland at ${brandName}.`,
    ` UK & Ireland competitions at ${brandName}.`,
  ]
  const tail = tails.find(t => line.length + t.length <= DESC_MAX)
  if (tail) line += tail

  // Only fold in the editor's copy when it actually adds something — for a prize like
  // "iPhone 17 Pro Max" the description is often just the prize name again.
  if (line.length < DESC_MIN && editorial && !line.toLowerCase().includes(editorial.toLowerCase())) {
    line += ` ${editorial}`
  }

  return truncateAtWord(line, DESC_MAX)
}

/** Absolute, query-free, no-trailing-slash canonical URL. */
export function buildCanonical(origin: string, path: string): string {
  const base = origin.replace(/\/+$/, '')
  const clean = (path || '/').split(/[?#]/)[0]
  if (clean === '/' || clean === '') return base + '/'
  return base + '/' + clean.replace(/^\/+/, '').replace(/\/+$/, '')
}
